import React, { useEffect, useState } from "react";
import PublicLayout from "../components/PublicLayout";
import VideoModal from "../components/VideoModal";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { ProgressBar } from "../components/ProgressBar";
import { PlayCircle, Instagram } from "lucide-react";

const PLACEHOLDER = "https://images.pexels.com/photos/5212339/pexels-photo-5212339.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600";

export default function Impact() {
  const { t } = useT();
  const [stats, setStats] = useState({ total_books_donated: 0, total_students_impacted: 0, total_schools: 0 });
  const [schools, setSchools] = useState([]);
  const [activity, setActivity] = useState([]);
  const [videos, setVideos] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(r => setStats(r.data));
    api.get("/public/schools").then(r => setSchools(r.data));
    api.get("/public/activity").then(r => setActivity(r.data));
    api.get("/public/impact-videos").then(r => setVideos(r.data)).catch(() => setVideos([]));
  }, []);

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("impact_overline")}</div>
        <h1 className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)] tracking-tight max-w-3xl">{t("impact_title")}</h1>
        <p className="mt-4 text-sm md:text-base text-[var(--bb-muted)] max-w-xl">{t("impact_sub")}</p>

        {/* 3 metrics only */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Big label={t("impact_books")} value={stats.total_books_donated.toLocaleString()} testid="impact-kpi-books"/>
          <Big label={t("impact_children")} value={stats.total_students_impacted.toLocaleString()} testid="impact-kpi-children"/>
          <Big label={t("impact_schools")} value={stats.total_schools.toLocaleString()} testid="impact-kpi-schools"/>
        </div>

        {/* Videos Grid */}
        <div className="mt-20" data-testid="impact-videos-section">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
            <h2 className="font-serif text-4xl text-[var(--bb-forest)] tracking-tight">{t("videos_title")}</h2>
            <Instagram size={20} className="text-[var(--bb-muted)]"/>
          </div>
          <p className="text-sm text-[var(--bb-muted)] mb-8 max-w-xl">{t("videos_sub")}</p>

          {videos.length === 0 ? (
            <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]" data-testid="impact-videos-empty">
              No impact videos available yet.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {videos.slice(0, visibleCount).map((v) => (
                  <button
                    key={v.video_id}
                    type="button"
                    onClick={() => setActiveVideo(v)}
                    className="group bb-card overflow-hidden block text-left cursor-pointer"
                    data-testid={`video-card-${v.video_id}`}
                  >
                    <div className="relative aspect-square bg-[var(--bb-sand)] overflow-hidden">
                      <img
                        src={v.thumbnail_url || PLACEHOLDER}
                        alt={v.title || "Impact video"}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                      />
                      <div className="absolute inset-0 bg-black/20 grid place-items-center opacity-80 group-hover:opacity-100 transition">
                        <PlayCircle size={48} className="text-white drop-shadow-lg" />
                      </div>
                    </div>
                    {v.title && (
                      <div className="p-3 text-sm text-[var(--bb-ink)] line-clamp-2">{v.title}</div>
                    )}
                  </button>
                ))}
              </div>
              {videos.length > visibleCount && (
                <div className="mt-6 text-center">
                  <button onClick={() => setVisibleCount(c => c + 6)} className="bb-btn-secondary" data-testid="videos-load-more">
                    {t("videos_load_more")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-20 grid md:grid-cols-2 gap-10">
          <div>
            <div className="bb-overline text-[var(--bb-muted)] mb-4">SCHOOLS BY PROGRESS</div>
            <div className="bb-card p-2">
              {schools.length === 0 ? <div className="p-8 text-center text-sm text-[var(--bb-muted)]">No schools yet.</div> : (
                <ul className="divide-y divide-[var(--bb-border)]">
                  {schools.map(s=>(
                    <li key={s.school_id} className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-serif text-lg text-[var(--bb-forest)]">{s.name}</div>
                        <div className="text-xs text-[var(--bb-muted)]">{s.total_books_funded||0} / {s.total_books_required||0}</div>
                      </div>
                      <ProgressBar value={s.total_books_funded||0} total={s.total_books_required||0}/>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <div className="bb-overline text-[var(--bb-muted)] mb-4">{t("feed_title").replace('.', '').toUpperCase()}</div>
            <div className="bb-card p-2">
              {activity.length === 0 ? <div className="p-8 text-center text-sm text-[var(--bb-muted)]">{t("feed_empty")}</div> : (
                <ul className="divide-y divide-[var(--bb-border)]">
                  {activity.map((a, i) => (
                    <li key={a.donation_id || a.id || `${a.donor_name}-${a.donation_date}-${i}`} className="p-4 text-sm">
                      <b>{a.donor_name}</b> · <b>{a.quantity}</b> sets · <span className="text-[var(--bb-forest)]">{a.school_name}</span>
                      <div className="text-xs text-[var(--bb-muted)] mt-1">{a.donation_date?.slice(0,10)}{a.dedication ? ` · "${a.dedication}"` : ""}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </PublicLayout>
  );
}

function Big({ label, value, testid }) {
  return (
    <div className="bb-card p-8" data-testid={testid}>
      <div className="bb-overline text-[var(--bb-muted)] mb-3">{label}</div>
      <div className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)]">{value}</div>
    </div>
  );
}
